import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import _ from '../../../utils/LodashImports';
import { noop } from '../../../utils/noop';
import { withProduct } from '../../../utils/ProductContext';
import ServicesPrices from '../../Prices/ServicesPrices';
import Warning from '../../Warning/Warning';
import {
  getSelectedIndex,
  getWarrantyBenefits,
  getWarrantyLabel,
  isWarrantyRecommended,
} from './WarrantySelect.helpers';
import { WarrantySelectEnhancedStyles } from './WarrantySelectEnhanced.style';

const WarrantySelectEnhanced = ({
  errorMessage,
  handleWarrantyOptionsSelected,
  warrantyOptionCurrent,
  options,
  appCtx,
  product,
  source = '',
}) => {
  const [benefitsVisibility, setBenefitsVisibility] = useState(false);
  const initialSelectionDone = useRef(false);

  const { deviceType, siteConfig, regionCode } = appCtx;
  const isMobile = deviceType === 'mobile';
  const gCategoryId = _.get(product, 'attributes.GSCCategoryId', '');
  const sellerName = _.get(product, 'variants[0].offerings[0].sellerName', '');

  const findRecommendedOptionIndex = () => {
    if (!options || !options.length) return -1;
    const recommendedIndex = options.findIndex((option) => option && option.name && isWarrantyRecommended(option.name));
    if (recommendedIndex >= 0) return recommendedIndex;
    if (options.length >= 2) return 1;
    return options.length > 0 ? 0 : -1;
  };

  const getInitialSelectedIndex = () => {
    if (warrantyOptionCurrent) {
      return getSelectedIndex({ options, warrantyOptionCurrent });
    }
    return findRecommendedOptionIndex();
  };

  const [selectedIndex, setSelectedIndex] = useState(getInitialSelectedIndex());
  useEffect(() => {
    if (!initialSelectionDone.current && options && options.length > 0 && !warrantyOptionCurrent) {
      const bestIndex = findRecommendedOptionIndex();

      if (bestIndex >= 0 && bestIndex < options.length) {
        setSelectedIndex(bestIndex);
        const selectedOption = { ...options[bestIndex], selected: true };
        handleWarrantyOptionsSelected(selectedOption);
        initialSelectionDone.current = true;
      }
    }
  }, [options, warrantyOptionCurrent, handleWarrantyOptionsSelected]);
  useEffect(() => {
    if (warrantyOptionCurrent) {
      const idx = getSelectedIndex({ options, warrantyOptionCurrent });
      setSelectedIndex(idx);
    }
  }, [warrantyOptionCurrent, options]);

  const {
    WARRANTIES_MODAL_LABEL = 'Extiende la garantía del producto',
    WARRANTY_BENEFITS_LABEL = 'Beneficios de extender la garantía',
    PDP_NO_THANKS = 'Sin garantía extendida',
    WARRANTY_LABEL_TEMPLATE = '{warrantyName}',
    RECOMMENDED_WARRANTY = '¡Recomendado!',
    MORE_WARRANTY_BENEFITS = 'Más beneficios',
  } = _.get(siteConfig, 'textDictionary', {});

  const titleText =
    source === 'extended-modal'
      ? _.get(siteConfig, 'textDictionary.EXTENDED_WARRANTY_TITLE', 'Protege tu producto')
      : WARRANTIES_MODAL_LABEL;

  const moreWarrantyBenefitsLink = _.get(siteConfig, 'endpoints.moreWarrantyBenefitsLink');
  const warranties = options.filter((option) => option.offeringId || option.value);

  const toggleBenefitsVisibility = () => {
    setBenefitsVisibility((prevState) => !prevState);
  };

  let warrantyBenefits = [];
  try {
    warrantyBenefits =
      getWarrantyBenefits({
        siteConfig,
        regionCode,
        gCategoryId,
      }) || [];
  } catch (error) {
    warrantyBenefits = [];
  }

  const handleOptionChange = (index) => {
    if (selectedIndex !== index) {
      setSelectedIndex(index);
      if (index < options.length) {
        const selectedOption = { ...options[index], selected: true };
        handleWarrantyOptionsSelected(selectedOption);
      }
    }
  };

  const renderWarrantySku = (warranty, index) => {
    const { name, textPrice, prices, discountBadge } = warranty;
    const warrantyLabel = getWarrantyLabel({
      name,
      sellerName,
      warrantyLabelTemplate: WARRANTY_LABEL_TEMPLATE,
    });
    const isSelected = index === selectedIndex;
    const isRecommended = isWarrantyRecommended(name);
    return (
      <>
        <button
          type="submit"
          id={`warranty-sku-${index}`}
          className={`warranty-sku ${isSelected ? 'selected' : ''}`}
          onClick={() => handleOptionChange(index)}
        >
          <div className="label-wrapper">
            <span className={`radio-button ${isSelected ? 'full' : 'empty'}`} />
            <div className="warranty-details">
              <p className="warranty-text">{warrantyLabel}</p>
              <div className="badges">
                {isRecommended && <p className="recommended-badge">{RECOMMENDED_WARRANTY}</p>}
              </div>
            </div>
          </div>
          {prices ? (
            <ServicesPrices priceList={prices} discountBadge={discountBadge} />
          ) : (
            <span className="price">{textPrice}</span>
          )}
        </button>
        <style jsx>{WarrantySelectEnhancedStyles}</style>
      </>
    );
  };

  return (
    <div className={`warranty-wrapper ${source}`}>
      <div className="title">
        <div className="warranty-icon" />
        <p>{titleText}</p>
      </div>
      <div className="body">
        <div className="sku-list">
          {warranties.map((warranty, index) => renderWarrantySku(warranty, index))}
          {isMobile && (
            <button
              type="submit"
              className={`warranty-sku ${warranties.length === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleOptionChange(warranties.length)}
            >
              <div className="label-wrapper">
                <span className={`radio-button ${selectedIndex === warranties.length ? 'full' : 'empty'}`} />
                <span className="warranty-details">
                  <span className="warranty-text">{PDP_NO_THANKS}</span>
                </span>
              </div>
            </button>
          )}
        </div>
        {warrantyBenefits && warrantyBenefits.length > 0 && (
          <div className="benefits-list">
            <div className={`title ${source}`}>
              <p>{WARRANTY_BENEFITS_LABEL}</p>
              {isMobile && source !== 'drawer' && (
                <button type="submit" onClick={toggleBenefitsVisibility}>
                  <i className={`${benefitsVisibility ? 'collapse' : 'expand'}`} />
                </button>
              )}
            </div>
            {(!isMobile || source === 'drawer' || benefitsVisibility) && (
              <div className="list">
                {warrantyBenefits.map((benefit) => (
                  <span className="benefit" key={`benefit-${benefit.replace(/\s+/g, '-').substring(0, 20)}`}>
                    <i className="tick-icon" />
                    <p>{benefit}</p>
                  </span>
                ))}
                {moreWarrantyBenefitsLink && (
                  <a
                    rel="noopener noreferrer"
                    target="_blank"
                    href={moreWarrantyBenefitsLink}
                    className="link-wrapper"
                    tabIndex={0}
                  >
                    <span className="more-benefits">{MORE_WARRANTY_BENEFITS}</span>
                    <i className="csicon-right" />
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {errorMessage && <Warning label={errorMessage} iconSize="copy2" labelSize="copy5" />}
      <style jsx>{WarrantySelectEnhancedStyles}</style>
    </div>
  );
};

WarrantySelectEnhanced.defaultProps = {
  warrantyOptionCurrent: null,
  errorMessage: null,
  handleWarrantyOptionsSelected: noop,
  options: [],
  appCtx: {},
  product: {},
  source: '',
};

WarrantySelectEnhanced.propTypes = {
  warrantyOptionCurrent: PropTypes.object,
  errorMessage: PropTypes.string,
  handleWarrantyOptionsSelected: PropTypes.func,
  options: PropTypes.array,
  appCtx: PropTypes.object,
  product: PropTypes.object,
  source: PropTypes.string,
};

export default withProduct(WarrantySelectEnhanced);
